import type { TileSectionProps } from "../../../common/TileSection";
import type {
  EnrichedJobDetailsPreference,
  JobDetailsSectionModel,
  SegmentPreference,
  JobDetailsPreference,
  SalaryInfoModel,
} from "../../types";

import {
  Clock,
  Jobs,
  MoneyStack,
  Commute,
  DocReview,
  Occupation,
  Publication,
  type IconProps,
} from "../../../../../../../../../mads";
import { type GettextInterface, useI18n } from "../../../../../../../../../shared/I18n";
import Sentiment from "../../../../../../../../../shared/util/salary/Sentiment";
import { useViewjobOneGraphModel } from "../../../../../../../context/ViewJobDataContext";
import { TruckingDetailsIcon } from "../../icons/TruckingDetailsIcon";
import { WorkSettingIcon } from "../../icons/WorkSettingIcon";
import { buildEnrichedJobDetailsPreferencesFrom } from "../preferenceCollectionUtils";
import { PreferenceType, usePreferenceTiles } from "../usePreferenceTiles";

type SectionProps = {
  preferenceList: EnrichedJobDetailsPreference[];
  sectionTitle: string;
  preferenceType: PreferenceType;
  icon?: React.FC<IconProps>;
  sectionSubtitle?: string;
  isOpen?: boolean;
  isTruncatable?: boolean;
  preferenceToTypeMap?: Map<string, PreferenceType>;
  preferenceToSegmentationSectionMap?: Map<string, SegmentationSection>;
};

export const enum SegmentationSection {
  MEDICAL_SPECIALTY = "Medical specialty",
  WORK_SETTING = "Work setting",
  TRUCKING_DETAILS = "Trucking details",
  TRAVEL_REQUIREMENTS = "Travel requirements",
  TEACHING_DETAILS = "Teaching details",
  PATIENT_DEMOGRAPHIC = "Patient demographic",
}

const segmentationPreferencesMap: { [_key in SegmentationSection]: React.ElementType } = {
  [SegmentationSection.MEDICAL_SPECIALTY]: Occupation,
  [SegmentationSection.WORK_SETTING]: WorkSettingIcon,
  [SegmentationSection.PATIENT_DEMOGRAPHIC]: DocReview,
  [SegmentationSection.TRUCKING_DETAILS]: TruckingDetailsIcon,
  [SegmentationSection.TEACHING_DETAILS]: Publication,
  [SegmentationSection.TRAVEL_REQUIREMENTS]: Commute,
};

const getInitialSalarySentiment = (salaryInfoModel: SalaryInfoModel) => {
  if (salaryInfoModel && salaryInfoModel.minimumPayPreferencePresent) {
    if (salaryInfoModel.matchingSalary) {
      return Sentiment.POSITIVE;
    } else {
      return Sentiment.NEGATIVE;
    }
  }

  return Sentiment.UNKNOWN;
};

const getPaySection = (
  knownUserSentimentIdMap: { [index: string]: string[] },
  trafficLightSignal: string,
  gt: GettextInterface,
  salaryInfoModel?: SalaryInfoModel,
) => {
  if (!salaryInfoModel) {
    return [];
  }

  const paySection = {
    preferenceList: [
      {
        prefIds: knownUserSentimentIdMap?.minimumPay ?? [],
        label: salaryInfoModel?.salaryText ?? "",
        sentiment: getInitialSalarySentiment(salaryInfoModel),
        suid: "minimumPay",
        customClassLabels: ["Pay Types"],
        customClassSuids: ["VFY2W"],
        salaryInfoModel,
        tooltips: [],
      },
    ],
    sectionTitle: gt.pgettext("Pay in job details section", "Pay"),
    preferenceType: PreferenceType.SALARY,
  };

  return [
    {
      ...paySection,
      icon: MoneyStack,
    },
  ];
};

const getJobTypeAndShiftScheduleSections = (
  jobTypes: JobDetailsPreference[] | null,
  shiftsAndSchedule: JobDetailsPreference[] | null,
  trafficLightSignal: string,
  gt: GettextInterface,
) => {
  jobTypes = jobTypes || [];
  shiftsAndSchedule = shiftsAndSchedule || [];
  return [
    !!jobTypes?.length && {
      preferenceList: jobTypes,
      sectionTitle: gt.pgettext("jobDetailsSectionJobTypeTitle", "Job type"),
      preferenceType: PreferenceType.JOB_TYPE,
      icon: Jobs,
    },
    !!shiftsAndSchedule?.length && {
      preferenceList: shiftsAndSchedule,
      sectionTitle: gt.pgettext("jobDetailsSectionShiftTitle", "Shift and schedule"),
      preferenceType: PreferenceType.SHIFT_AND_SCHEDULE,
      icon: Clock,
    },
  ].filter(Boolean);
};

const getSegmentationSection = (
  segmentPreferences: SegmentPreference[],
  contents: { [index: string]: string[] },
  knownUserSentimentIdMap: { [index: string]: string[] },
  shouldShowSegmentationSection: boolean,
) => {
  if (!shouldShowSegmentationSection) {
    return [];
  }
  return shouldShowSegmentationSection
    ? segmentPreferences?.map((segPrefModel) => ({
        preferenceList: buildEnrichedJobDetailsPreferencesFrom(
          segPrefModel,
          knownUserSentimentIdMap,
        ),
        sectionTitle: segPrefModel.displayTitle,
        preferenceType: PreferenceType.SEGMENTATION,
        icon: segmentationPreferencesMap[segPrefModel.displayTitle as SegmentationSection],
      }))
    : [];
};

export const usePreferenceSections = ({
  jobDetailsSection,
  onAddError,
  rtl,
  trafficLightSignal,
  knownUserSentimentIdMap = {},
  salaryInfoModel,
}: {
  jobDetailsSection: JobDetailsSectionModel;
  onAddError: () => void;
  rtl: boolean;
  trafficLightSignal: string;
  knownUserSentimentIdMap: { [index: string]: string[] };
  salaryInfoModel?: SalaryInfoModel;
}): {
  sections: TileSectionProps[];
  shouldShowSegmentationSection: boolean;
} => {
  const {
    highlightedAttributesSectionModels,
    jobTypes,
    shiftsAndSchedule,
    segmentPreferences,
    contents,
  } = jobDetailsSection;
  const gt = useI18n();
  const { accountKey } = useViewjobOneGraphModel() ?? {};

  const shouldShowSegmentationSection: boolean =
    !!segmentPreferences?.length || !!highlightedAttributesSectionModels?.length;

  const sections = [
    ...getPaySection(knownUserSentimentIdMap, trafficLightSignal, gt, salaryInfoModel),
    ...getJobTypeAndShiftScheduleSections(
      jobTypes ?? [],
      shiftsAndSchedule ?? [],
      trafficLightSignal,
      gt,
    ),
    ...getSegmentationSection(
      segmentPreferences ?? [],
      contents,
      knownUserSentimentIdMap,
      shouldShowSegmentationSection,
    ),
  ].filter(Boolean) as SectionProps[];

  return {
    sections: sections.map((section) => {
      return {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        tiles: usePreferenceTiles({
          accountKey: accountKey ?? "",
          knownUserSentimentIdMap,
          onAddError,
          ...section,
        }),
        title: section.sectionTitle,
        type: section.preferenceType,
        icon: section.icon,
        subtitleProps: section.sectionSubtitle
          ? {
              rtl,
              label: section.sectionSubtitle,
              linkAriaLabel: gt.gettext("job preferences (opens in a new window)"),
            }
          : undefined,
        isOpen: section.isOpen ?? true,
        isTruncatable: section.isTruncatable ?? false,
      };
    }),
    shouldShowSegmentationSection,
  };
};
